import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import config from '../../config/config';
import byggnadsvardImage from '../../assets/images/byggnadsvard.jpg';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false
  });
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');

  const handleClickShowPassword = (field: 'password' | 'confirmPassword') => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (credentials.password !== credentials.confirmPassword) {
      setError('Lösenorden matchar inte');
      return;
    }

    if (credentials.password.length < 8) {
      setError('Lösenordet måste vara minst 8 tecken långt');
      return;
    }

    try {
      const response = await fetch(`${config.api.baseUrl}/auth/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({ password: credentials.password })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Kunde inte sätta lösenord');
      }

      login(data.token);
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Set password error:', err);
      setError(err instanceof Error ? err.message : 'Ett fel uppstod när lösenordet skulle sättas');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${config.api.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: credentials.username,
          ...(credentials.password && { password: credentials.password })
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Inloggningen misslyckades');
      }

      if (data.isFirstLogin) {
        setIsSettingPassword(true);
        setTempToken(data.token);
        setCredentials(prev => ({
          ...prev,
          password: '',
          confirmPassword: ''
        }));
        return;
      }

      login(data.token);
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Ett fel uppstod vid inloggning');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${byggnadsvardImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.95)'
          }}
        >
          <Typography variant="h5" component="h1" gutterBottom align="center">
            {isSettingPassword ? 'Välj ditt lösenord' : 'Admin Inloggning'}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={isSettingPassword ? handleSetPassword : handleLogin}>
            {!isSettingPassword ? (
              <>
                <TextField
                  fullWidth
                  label="Användarnamn"
                  name="username"
                  value={credentials.username}
                  onChange={handleChange}
                  margin="normal"
                  required
                  autoFocus
                />
                <TextField
                  fullWidth
                  label="Lösenord"
                  name="password"
                  type={showPassword.password ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleChange}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('password')}
                          edge="end"
                        >
                          {showPassword.password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Välj lösenord"
                  name="password"
                  type={showPassword.password ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleChange}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('password')}
                          edge="end"
                        >
                          {showPassword.password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Bekräfta lösenord"
                  name="confirmPassword"
                  type={showPassword.confirmPassword ? 'text' : 'password'}
                  value={credentials.confirmPassword}
                  onChange={handleChange}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('confirmPassword')}
                          edge="end"
                        >
                          {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
            >
              {isSettingPassword ? 'Spara lösenord' : 'Logga in'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login; 